import React, { useState, useEffect, useCallback } from 'react'
import {
  MapPin, Plus, Search, X, Edit2, RefreshCw, Upload, AlertTriangle, BarChart3, Target,
  Trash2, LogOut as ExitIcon
} from 'lucide-react'
import ImportModal from '../../../components/shared/ImportModal'
import ImportIAModal from '../../../components/shared/ImportIAModal'
import { ScoreBadge } from '../../../components/shared/ScoreBadge'

const API_URL = process.env.REACT_APP_BACKEND_URL

export default function SitesPage({ tenants, readOnly = false, forceTenantId = null }) {
  const [sites, setSites] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterTenant, setFilterTenant] = useState(forceTenantId || '')
  const [filterStatut, setFilterStatut] = useState('actif')
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)
  const [showDetail, setShowDetail] = useState(null)
  const [detailData, setDetailData] = useState(null)
  const [showImport, setShowImport] = useState(false)
  const [showImportIA, setShowImportIA] = useState(false)
  const [analytics, setAnalytics] = useState(null)
  const [scores, setScores] = useState({})
  const [selectedIds, setSelectedIds] = useState([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const fetchSites = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filterTenant) params.set('tenant_id', filterTenant)
      if (filterStatut) params.set('statut', filterStatut)
      if (search) params.set('search', search)
      const res = await fetch(`${API_URL}/api/sites?${params}`)
      if (res.ok) {
        const d = await res.json()
        setSites(d.sites || [])
      }
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [filterTenant, filterStatut, search])

  useEffect(() => { fetchSites() }, [fetchSites])

  async function handleBulkAction(action) {
    const actionLabel = action === 'fermer' ? 'fermer' : 'supprimer définitivement'
    const confirmed = window.confirm(
      `Voulez-vous ${actionLabel} ${selectedIds.length} site(s) ?\n\n${
        action === 'supprimer' ? '⚠ Cette action est irréversible.' : 'Les sites seront marqués comme fermés.'
      }`
    )
    if (!confirmed) return
    setBulkLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/sites/bulk-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds, action })
      })
      const data = await res.json()
      if (data.success) {
        alert(`${data.traites} site(s) traité(s).${data.erreurs > 0 ? ` ${data.erreurs} erreur(s).` : ''}`)
        setSelectedIds([])
        fetchSites()
      }
    } catch (e) { alert('Erreur lors du traitement') }
    finally { setBulkLoading(false) }
  }

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const params = new URLSearchParams()
        if (filterTenant) params.set('tenant_id', filterTenant)
        const endpoint = readOnly ? `/api/analytics/sites/daf/${filterTenant || ''}` : `/api/analytics/sites?${params}`
        if (readOnly && !filterTenant) return
        const res = await fetch(`${API_URL}${endpoint}`)
        if (res.ok) setAnalytics(await res.json())
      } catch (e) { console.error(e) }
    }
    const fetchScores = async () => {
      try {
        const params = new URLSearchParams()
        if (filterTenant) params.set('tenant_id', filterTenant)
        const res = await fetch(`${API_URL}/api/scoring/sites?${params}`)
        if (res.ok) {
          const d = await res.json()
          const map = {}
          for (const s of d.scores || []) map[s.entity_id] = s
          setScores(map)
        }
      } catch (e) { console.error(e) }
    }
    fetchAnalytics()
    fetchScores()
  }, [filterTenant, readOnly])

  const fetchDetail = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/sites/${id}`)
      if (res.ok) setDetailData(await res.json())
    } catch (e) { console.error(e) }
  }

  useEffect(() => {
    if (showDetail) fetchDetail(showDetail)
  }, [showDetail])

  return (
    <div className="p-6 space-y-5 max-w-[1400px]" data-testid="sites-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800" data-testid="sites-title">Sites assurés</h1>
          <p className="text-sm text-slate-500">Gestion des sites et locaux assurés</p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowImport(true)} className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-testid="sites-import-btn">
              <Upload size={14} /> Import
            </button>
            <button onClick={() => setShowImportIA(true)} className="flex items-center gap-1.5 text-sm px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors" data-testid="sites-import-ia-btn">
              <Upload size={14} /> Import IA
            </button>
            <button onClick={() => { setEditing(null); setShowForm(true) }} className="flex items-center gap-1.5 text-sm px-3 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors" data-testid="sites-add-btn">
              <Plus size={14} /> Ajouter
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher (nom, ville, adresse)..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg"
            data-testid="sites-search"
          />
        </div>
        {tenants?.length > 0 && !forceTenantId && (
          <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="sites-filter-tenant">
            <option value="">Tous les clients</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
          </select>
        )}
        <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="sites-filter-statut">
          <option value="">Tous statuts</option>
          <option value="actif">Actif</option>
          <option value="inactif">Inactif</option>
        </select>
        <span className="text-xs text-slate-400">{sites.length} site(s)</span>
      </div>

      {/* Analytics block */}
      {analytics && (analytics.nb_sites_sinistres > 0 || analytics.alertes?.length > 0) && (
        <SiteAnalyticsBlock data={analytics} readOnly={readOnly} />
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : sites.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">Aucun site trouvé</p>
            <p className="text-xs text-slate-400 mt-1">{!readOnly ? "Ajoutez votre premier site ou importez un fichier" : "Les sites seront disponibles après création"}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Bulk action bar */}
            {selectedIds.length > 0 && !readOnly && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 mb-3 flex items-center justify-between" data-testid="sites-bulk-bar">
                <span className="text-sm font-medium text-indigo-700">{selectedIds.length} site(s) sélectionné(s)</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleBulkAction('fermer')} disabled={bulkLoading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:opacity-50"
                    data-testid="sites-bulk-fermer"><ExitIcon size={12} /> Fermer ({selectedIds.length})</button>
                  <button onClick={() => handleBulkAction('supprimer')} disabled={bulkLoading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                    data-testid="sites-bulk-delete"><Trash2 size={12} /> Supprimer ({selectedIds.length})</button>
                  <button onClick={() => setSelectedIds([])} className="text-xs text-slate-500 hover:text-slate-700 ml-2">Annuler</button>
                </div>
              </div>
            )}
            <table className="w-full text-xs" data-testid="sites-table">
              <thead>
                <tr className="bg-slate-50/80 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                  {!readOnly && (
                    <th className="px-3 py-2.5 text-center w-8">
                      <input type="checkbox" checked={selectedIds.length > 0 && selectedIds.length === sites.length}
                        onChange={e => setSelectedIds(e.target.checked ? sites.map(s => s.id) : [])}
                        className="w-3.5 h-3.5 rounded border-slate-300" data-testid="sites-select-all" />
                    </th>
                  )}
                  <th className="px-4 py-2.5 text-left">Nom</th>
                  <th className="px-4 py-2.5 text-left">Adresse</th>
                  <th className="px-4 py-2.5 text-left">CP / Ville</th>
                  {!forceTenantId && <th className="px-4 py-2.5 text-left">Client</th>}
                  <th className="px-4 py-2.5 text-left">Police</th>
                  <th className="px-4 py-2.5 text-center">Statut</th>
                  <th className="px-4 py-2.5 text-center">Score</th>
                  {!readOnly && <th className="px-4 py-2.5 text-center">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sites.map((site, i) => (
                  <tr key={site.id} className={`hover:bg-slate-50 transition-colors cursor-pointer ${selectedIds.includes(site.id) ? 'bg-indigo-50/50' : ''}`} onClick={() => setShowDetail(site.id)} data-testid={`site-row-${i}`}>
                    {!readOnly && (
                      <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.includes(site.id)}
                          onChange={e => { if (e.target.checked) setSelectedIds(p => [...p, site.id]); else setSelectedIds(p => p.filter(id => id !== site.id)) }}
                          className="w-3.5 h-3.5 rounded border-slate-300" data-testid={`site-checkbox-${i}`} />
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-semibold text-slate-800">{site.nom}</td>
                    <td className="px-4 py-2.5 text-slate-600">{site.adresse || '—'}</td>
                    <td className="px-4 py-2.5 text-slate-600">{[site.code_postal, site.ville].filter(Boolean).join(' ') || '—'}</td>
                    {!forceTenantId && <td className="px-4 py-2.5 text-slate-600">{site.tenants?.nom || '—'}</td>}
                    <td className="px-4 py-2.5 text-slate-500">{site.polices?.numero_police || '—'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${site.statut === 'actif' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{site.statut}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {scores[site.id] ? (
                        <ScoreBadge score={scores[site.id].score} niveau={scores[site.id].niveau} size="xs" />
                      ) : <span className="text-[10px] text-slate-300">—</span>}
                    </td>
                    {!readOnly && (
                      <td className="px-4 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        <button onClick={() => { setEditing(site); setShowForm(true) }} className="p-1 hover:bg-slate-100 rounded" data-testid={`site-edit-${i}`}><Edit2 size={12} className="text-slate-500" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form modal */}
      {showForm && !readOnly && (
        <SiteForm
          tenants={tenants}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null) }}
          onSave={() => { setShowForm(false); setEditing(null); fetchSites() }}
        />
      )}

      {/* Import modal */}
      {showImport && !readOnly && (
        <ImportModal entityType="sites" tenants={tenants} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); fetchSites() }} />
      )}

      {/* Import IA modal */}
      <ImportIAModal
        isOpen={showImportIA}
        onClose={() => setShowImportIA(false)}
        tenantId={forceTenantId || filterTenant}
        policeId={null}
        importType="sites"
        onSuccess={() => { setShowImportIA(false); fetchSites() }}
      />

      {/* Detail panel */}
      {showDetail && detailData && (
        <SiteDetailPanel data={detailData} onClose={() => { setShowDetail(null); setDetailData(null) }} />
      )}
    </div>
  )
}

// ── Site Analytics Block ──────────────────────────────────────────────────
function SiteAnalyticsBlock({ data, readOnly }) {
  const fmt = (n) => n != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n) : '—'

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="site-analytics">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <BarChart3 size={16} className="text-emerald-600" />
        <h2 className="text-sm font-semibold text-slate-700">Analytique sinistralité sites</h2>
        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{data.seuil_alerte}</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Sites sinistrés</p>
            <p className="text-xl font-bold text-slate-800">{data.nb_sites_sinistres} <span className="text-xs font-normal text-slate-400">/ {data.nb_sites_total}</span></p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Sinistres rattachés</p>
            <p className="text-xl font-bold text-slate-800">{data.nb_sinistres_rattaches}</p>
            {data.nb_sinistres_sans_site > 0 && <p className="text-[10px] text-slate-400">{data.nb_sinistres_sans_site} non rattachés</p>}
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Coût total</p>
            <p className="text-xl font-bold text-slate-800">{fmt(data.cout_total)}</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Coût moyen / site</p>
            <p className="text-xl font-bold text-slate-800">{fmt(data.cout_moyen_par_site)}</p>
          </div>
        </div>

        {data.alertes?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Alertes détectées</p>
            <div className="space-y-1.5">
              {data.alertes.slice(0, 5).map((a, i) => (
                <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg ${a.niveau === 'critical' ? 'bg-red-50' : 'bg-amber-50'}`} data-testid={`site-alert-${i}`}>
                  <AlertTriangle size={14} className={`mt-0.5 shrink-0 ${a.niveau === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${a.niveau === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {a.type === 'frequence_anormale' ? 'FRÉQUENCE' : 'COÛT'}
                      </span>
                      <span className="text-xs font-semibold text-slate-700">{a.entity_label}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-0.5">{a.explication}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.concentration?.concentration_detected && (
          <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100" data-testid="site-concentration">
            <div className="flex items-center gap-2 mb-1">
              <Target size={14} className="text-indigo-600" />
              <span className="text-xs font-semibold text-indigo-700">Concentration détectée</span>
            </div>
            <p className="text-[11px] text-indigo-600">{data.concentration.explication}</p>
          </div>
        )}

        {data.top_sites?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Top sites sinistrés</p>
            <div className="space-y-1">
              {data.top_sites.slice(0, 5).map((s, i) => (
                <div key={s.site_id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg" data-testid={`top-site-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold text-slate-400 w-4">{i + 1}</span>
                    <span className="text-xs font-semibold text-slate-800">{s.nom}</span>
                    {s.ville && <span className="text-[10px] text-slate-500">— {s.ville}</span>}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <span>{s.nb_sinistres} sin.</span>
                    <span className="font-semibold text-slate-800">{fmt(s.cout_total)}</span>
                    <span className="text-[10px] text-slate-400">{s.nb_ouverts} ouv. / {s.nb_clos} clos</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Site Form ──────────────────────────────────────────────────────────────
function SiteForm({ tenants, editing, onClose, onSave }) {
  const [form, setForm] = useState({
    tenant_id: editing?.tenant_id || (tenants?.[0]?.id || ''),
    police_id: editing?.police_id || '',
    nom: editing?.nom || '',
    adresse: editing?.adresse || '',
    code_postal: editing?.code_postal || '',
    ville: editing?.ville || '',
    pays: editing?.pays || 'France',
    notes: editing?.notes || '',
  })
  const [polices, setPolices] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (form.tenant_id) {
      fetch(`${API_URL}/api/polices?tenant_id=${form.tenant_id}`)
        .then(r => r.json())
        .then(d => setPolices(Array.isArray(d) ? d : d.polices || []))
        .catch(() => {})
    }
  }, [form.tenant_id])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const url = editing ? `${API_URL}/api/sites/${editing.id}` : `${API_URL}/api/sites`
      const method = editing ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (res.ok) { onSave() }
      else {
        const d = await res.json()
        setError(d.detail || 'Erreur lors de la sauvegarde')
      }
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="site-form-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">{editing ? 'Modifier le site' : 'Ajouter un site'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-xs rounded-lg">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Client *</label>
              <select value={form.tenant_id} onChange={e => setForm({ ...form, tenant_id: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" required data-testid="site-form-tenant">
                {tenants?.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Nom du site *</label>
              <input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="Entrepôt Nord" required data-testid="site-form-nom" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Adresse</label>
              <input value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="12 rue de l'Industrie" data-testid="site-form-adresse" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Code postal</label>
              <input value={form.code_postal} onChange={e => setForm({ ...form, code_postal: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="75001" data-testid="site-form-cp" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Ville</label>
              <input value={form.ville} onChange={e => setForm({ ...form, ville: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="Paris" data-testid="site-form-ville" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Pays</label>
              <input value={form.pays} onChange={e => setForm({ ...form, pays: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="site-form-pays" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Police liée</label>
              <select value={form.police_id} onChange={e => setForm({ ...form, police_id: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="site-form-police">
                <option value="">Aucune</option>
                {polices.map(p => <option key={p.id} value={p.id}>{p.numero_police} — {p.libelle}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 h-16" data-testid="site-form-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Annuler</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50" data-testid="site-form-submit">
              {saving ? 'Enregistrement...' : editing ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Site Detail Panel ──────────────────────────────────────────────────────
function SiteDetailPanel({ data, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-end sm:items-center justify-center z-50" data-testid="site-detail-panel">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg mx-0 sm:mx-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-semibold text-slate-800">{data.nom}</h3>
            <p className="text-xs text-slate-500">{[data.adresse, data.code_postal, data.ville].filter(Boolean).join(', ')}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div><p className="text-[10px] text-slate-400 uppercase">Client</p><p className="font-medium text-slate-700 mt-0.5">{data.tenants?.nom || '—'}</p></div>
            <div><p className="text-[10px] text-slate-400 uppercase">Police</p><p className="font-medium text-slate-700 mt-0.5">{data.polices?.numero_police || '—'}</p></div>
            <div><p className="text-[10px] text-slate-400 uppercase">Pays</p><p className="font-medium text-slate-700 mt-0.5">{data.pays || 'France'}</p></div>
            <div><p className="text-[10px] text-slate-400 uppercase">Statut</p><p className="font-medium text-slate-700 mt-0.5">{data.statut}</p></div>
          </div>
          {data.notes && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-slate-600">{data.notes}</p></div>}
          {data.sinistres?.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Sinistres sur ce site ({data.sinistres.length})</p>
              <div className="space-y-1.5">
                {data.sinistres.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">{s.numero_sinistre}</p>
                      <p className="text-slate-500">{s.nature_sinistre || s.type_sinistre}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-700">{s.montant_dommages_estime ? `${Number(s.montant_dommages_estime).toLocaleString('fr-FR')} €` : '—'}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${s.statut === 'clos' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{s.statut}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-center text-slate-400 py-4">Aucun sinistre rattaché à ce site</p>
          )}
        </div>
      </div>
    </div>
  )
}
